/**
 * Coyote Color — Wholesale display-only price adjustment (Layer 2)
 * ------------------------------------------------------------------
 * PURPOSE
 * This script ONLY changes what a wholesale-tagged customer SEES on the
 * product page before they add to cart. It never touches what actually
 * gets charged — that is guaranteed separately, server-side, by a
 * Shopify Discount Function (Layer 1) that applies regardless of
 * anything this script does or fails to do.
 *
 * If this script has a bug, is blocked, or Option Calculator changes
 * its markup and this stops matching anything: the customer may see
 * the wrong number for a moment, but checkout still charges exactly
 * 70% of whatever Option Calculator calculated. This script is a
 * cosmetic convenience layer only, by design.
 *
 * WHY THIS APPROACH
 * Option Calculator renders as a Shopify app block (theme app
 * extension). Its markup/JS lives outside this theme entirely, so
 * there is no supported event or API this theme can subscribe to.
 * This uses a scoped MutationObserver to catch price text as it
 * renders/updates, which is the safest available fallback given that
 * constraint. It is intentionally conservative:
 *   - Only runs at all for customers with data-wholesale-customer="true"
 *     on <body>, which is rendered server-side via Liquid from the
 *     REAL authenticated customer's tags. Retail/logged-out customers
 *     never even load this logic.
 *   - Only scans inside the product form / app block area on product
 *     pages, not the whole document.
 *   - Tracks each price element's last-seen "original" value so it
 *     multiplies the *true* calculated price by 0.7 every time it
 *     changes, rather than compounding a discount on top of itself.
 */
(function () {
  'use strict';

  var IS_WHOLESALE = document.body && document.body.dataset.wholesaleCustomer === 'true';
  if (!IS_WHOLESALE) return; // Retail / logged-out customers: do nothing at all.

  var WHOLESALE_MULTIPLIER = 0.70;
  var MONEY_RE = /\$\s?\d[\d,]*\.\d{2}/; // matches "$147.50", "$1,234.56", etc.

  function formatMoney(value) {
    // Matches typical Shopify money formatting: $1,234.56
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function parseMoney(text) {
    var match = text.match(MONEY_RE);
    if (!match) return null;
    return parseFloat(match[0].replace(/[^0-9.]/g, ''));
  }

  function processElement(el) {
    if (!el || el.children.length > 0) return; // only handle leaf nodes (no nested elements)
    var text = el.textContent;
    if (!text) return;
    if (!MONEY_RE.test(text)) return;

    var currentValue = parseMoney(text);
    if (currentValue === null) return;

    var lastDiscounted = el.dataset.wholesaleDiscounted
      ? parseFloat(el.dataset.wholesaleDiscounted)
      : null;

    // If the currently-displayed value already equals the discounted
    // value we set last time, this mutation was caused by our own
    // edit — skip it to avoid an infinite loop / re-discounting.
    if (lastDiscounted !== null && Math.abs(currentValue - lastDiscounted) < 0.005) {
      return;
    }

    var originalValue = currentValue;
    var discountedValue = originalValue * WHOLESALE_MULTIPLIER;

    el.dataset.wholesaleOriginal = originalValue.toFixed(2);
    el.dataset.wholesaleDiscounted = discountedValue.toFixed(2);

    // Replace only the matched money substring, preserving any
    // surrounding text (labels, "ea.", etc.) the element might have.
    el.textContent = text.replace(MONEY_RE, formatMoney(discountedValue));
  }

  function scan(root) {
    if (!root) return;
    if (root.nodeType !== 1) return;
    if (root.children.length === 0) {
      processElement(root);
      return;
    }
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
    var node = walker.currentNode;
    while (node) {
      if (node.children.length === 0) processElement(node);
      node = walker.nextNode();
    }
  }

  function findScanRoots() {
    // Prefer the product form (native theme markup) — this contains
    // both the (disabled) native price block area and the Option
    // Calculator app block, since both are rendered inside the same
    // <product-form> element per section/product-main.liquid.
    var roots = document.querySelectorAll('product-form, .product-main, [id^="shopify-block-"]');
    return roots.length ? roots : [document.body];
  }

  function init() {
    var roots = findScanRoots();
    roots.forEach(function (root) { scan(root); });

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'characterData' && mutation.target.parentElement) {
          processElement(mutation.target.parentElement);
        } else if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) scan(node);
          });
        }
      });
    });

    roots.forEach(function (root) {
      observer.observe(root, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
